/**
 * Google Analytics and Google Tag Manager Utility
 * Handles event tracking, user properties, and GA/GTM integration
 */

/**
 * Initialize GA with GTM (GA is loaded via GTM)
 * Call this once when the app loads
 */
export const initializeGA = () => {
  if (typeof window === 'undefined') return;
  
  // GA 4 is typically loaded via GTM, but we can add GA initialization here if needed
  window.dataLayer = window.dataLayer || [];
  
  // Set GA4 measurement ID if available
  const gaId = process.env.GA_MEASUREMENT_ID;
  if (gaId) {
    console.debug('GA initialization: Measurement ID', gaId);
  }
};

/**
 * Track a page view event
 * @param {string} pagePath - The page path (e.g., '/products/checkout')
 * @param {string} pageTitle - The page title (e.g., 'Checkout')
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('gtag not available for pageView tracking');
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    timestamp: new Date().toISOString()
  });

  console.debug('📊 GA PageView:', { pagePath, pageTitle });
};

/**
 * Track a custom event
 * @param {string} eventName - The event name (e.g., 'add_to_cart', 'purchase')
 * @param {object} eventData - Event parameters
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('gtag not available for event tracking:', eventName);
    return;
  }

  window.gtag('event', eventName, {
    ...eventData,
    timestamp: new Date().toISOString()
  });

  console.debug(`📊 GA Event - ${eventName}:`, eventData);
};

/**
 * Track e-commerce event: Add to Cart
 * @param {object} item - Item details {id, name, price, quantity, currency}
 */
export const trackAddToCart = (item) => {
  trackEvent('add_to_cart', {
    currency: item.currency || 'USD',
    value: item.price || 0,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity || 1
      }
    ]
  });
};

/**
 * Track e-commerce event: Remove from Cart
 * @param {object} item - Item details {id, name, price, quantity, currency}
 */
export const trackRemoveFromCart = (item) => {
  trackEvent('remove_from_cart', {
    currency: item.currency || 'USD',
    value: item.price || 0,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity || 1
      }
    ]
  });
};

/**
 * Track e-commerce event: View Cart
 * @param {array} items - Array of items in cart
 * @param {number} cartValue - Total cart value
 * @param {string} currency - Currency code
 */
export const trackViewCart = (items = [], cartValue = 0, currency = 'USD') => {
  trackEvent('view_cart', {
    currency,
    value: cartValue,
    items: items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity || 1
    }))
  });
};

/**
 * Track e-commerce event: Begin Checkout
 * @param {object} checkoutData - Checkout data {items, value, currency}
 */
export const trackBeginCheckout = (checkoutData = {}) => {
  trackEvent('begin_checkout', {
    currency: checkoutData.currency || 'USD',
    value: checkoutData.value || 0,
    items: checkoutData.items || []
  });
};

/**
 * Track e-commerce event: Add Shipping Info
 * @param {object} shippingData - Shipping data {method, value, currency}
 */
export const trackAddShippingInfo = (shippingData = {}) => {
  trackEvent('add_shipping_info', {
    currency: shippingData.currency || 'USD',
    value: shippingData.value || 0,
    shipping_tier: shippingData.method || 'standard'
  });
};

/**
 * Track e-commerce event: Add Payment Info
 * @param {object} paymentData - Payment data {method, currency, value}
 */
export const trackAddPaymentInfo = (paymentData = {}) => {
  trackEvent('add_payment_info', {
    currency: paymentData.currency || 'USD',
    payment_type: paymentData.method || 'credit_card'
  });
};

/**
 * Track e-commerce event: Purchase
 * @param {object} purchaseData - Purchase data {orderId, items, value, currency, tax, shipping}
 */
export const trackPurchase = (purchaseData = {}) => {
  trackEvent('purchase', {
    transaction_id: purchaseData.orderId,
    currency: purchaseData.currency || 'USD',
    value: purchaseData.value || 0,
    tax: purchaseData.tax || 0,
    shipping: purchaseData.shipping || 0,
    items: purchaseData.items || []
  });
};

/**
 * Track e-commerce event: View Item
 * @param {object} item - Item details {id, name, price, category, currency}
 */
export const trackViewItem = (item) => {
  trackEvent('view_item', {
    currency: item.currency || 'USD',
    value: item.price || 0,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price
      }
    ]
  });
};

/**
 * Track e-commerce event: View Item List
 * @param {object} listData - List data {listName, items}
 */
export const trackViewItemList = (listData) => {
  trackEvent('view_item_list', {
    items: (listData.items || []).map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category,
      price: item.price
    })),
    item_list_name: listData.listName
  });
};

/**
 * Track search event
 * @param {string} searchTerm - The search term
 * @param {number} resultsCount - Number of results
 */
export const trackSearch = (searchTerm, resultsCount = 0) => {
  trackEvent('search', {
    search_term: searchTerm,
    number_of_results: resultsCount
  });
};

/**
 * Track user login event
 * @param {string} loginMethod - Login method (e.g., 'email', 'sso', 'msal')
 */
export const trackLogin = (loginMethod = 'email') => {
  trackEvent('login', {
    method: loginMethod
  });
};

/**
 * Track user signup/registration event
 * @param {string} signupMethod - Signup method (e.g., 'email', 'sso')
 */
export const trackSignup = (signupMethod = 'email') => {
  trackEvent('sign_up', {
    method: signupMethod
  });
};

/**
 * Track error events
 * @param {string} errorType - Type of error (e.g., 'auth_error', 'network_error')
 * @param {string} errorMessage - Error message
 */
export const trackError = (errorType, errorMessage) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage
  });
};

/**
 * Track custom exception
 * @param {string} description - Exception description
 * @param {boolean} fatal - Whether the exception was fatal
 */
export const trackException = (description, fatal = false) => {
  trackEvent('exception', {
    description,
    fatal
  });
};

/**
 * Set user properties for GA4
 * @param {object} userProperties - User properties to set
 */
export const setUserProperties = (userProperties = {}) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('gtag not available for setting user properties');
    return;
  }

  window.gtag('set', {
    user_id: userProperties.userId,
    user_properties: {
      user_role: userProperties.role,
      user_type: userProperties.type,
      company_id: userProperties.companyId
    }
  });

  console.debug('📊 GA User Properties Set:', userProperties);
};

/**
 * Track form submission/interaction
 * @param {string} formName - Name of the form
 * @param {string} formAction - Action taken (e.g., 'submit', 'abandon')
 */
export const trackFormInteraction = (formName, formAction = 'submit') => {
  trackEvent('form_interaction', {
    form_name: formName,
    form_action: formAction
  });
};

/**
 * Track button/link click events
 * @param {string} buttonName - Name/label of the button
 * @param {string} buttonCategory - Category of button
 */
export const trackButtonClick = (buttonName, buttonCategory = 'general') => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_category: buttonCategory
  });
};

/**
 * Track modal/dialog open events
 * @param {string} modalName - Name of the modal
 */
export const trackModalOpen = (modalName) => {
  trackEvent('modal_open', {
    modal_name: modalName
  });
};

/**
 * Track modal/dialog close events
 * @param {string} modalName - Name of the modal
 */
export const trackModalClose = (modalName) => {
  trackEvent('modal_close', {
    modal_name: modalName
  });
};

/**
 * Track file download events
 * @param {string} fileName - Name of the file
 * @param {string} fileType - Type of file (e.g., 'pdf', 'excel')
 */
export const trackFileDownload = (fileName, fileType) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType
  });
};

/**
 * Send event to GTM data layer directly
 * @param {object} eventData - Event data to push to dataLayer
 */
export const sendToDataLayer = (eventData) => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventData);

  console.debug('📊 Sent to GTM dataLayer:', eventData);
};

export default {
  initializeGA,
  trackPageView,
  trackEvent,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  trackBeginCheckout,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackPurchase,
  trackViewItem,
  trackViewItemList,
  trackSearch,
  trackLogin,
  trackSignup,
  trackError,
  trackException,
  setUserProperties,
  trackFormInteraction,
  trackButtonClick,
  trackModalOpen,
  trackModalClose,
  trackFileDownload,
  sendToDataLayer
};
